/**
 * The reader saying "go".
 *
 * When a run lands, the assistant's announcement names the next step of the
 * plan and asks for a plain `go` (backend `workflowRunCompletionRecommendation`).
 * That promise has to be kept here: without this, "go" is just another
 * question, the assistant answers it as prose, and the step the reader agreed
 * to never starts. This is the conversational half of the plan card, so the
 * two affordances do the same thing.
 */

import { isWorkflowRunCompletionAskId } from './workflowRunCompletion'

interface GoIntentPlan {
  lifecycle: string
  /** Null while a plan is still proposed: no step is current yet. */
  currentIndex: number | null
  steps: Array<{ status: string }>
}

interface GoIntentTurn {
  id: string
  role: string
  /** Persisted ask id; `workflow-run-complete:<runToken>` on an announcement. */
  askId?: string | null
  /** Set when this turn is a plan step's own answer; names that plan's card. */
  planMessageId?: string
}

export type ThreadGoTarget =
  | { messageId: string; mode: 'confirm' }
  | { messageId: string; mode: 'start' }

/**
 * Whether this message is the reader agreeing, and nothing else.
 *
 * Deliberately narrow. "go" on its own is an answer to the recommendation;
 * "go deeper on the chilled set" is a question, and swallowing it would lose
 * the reader's actual ask and start work they did not agree to. Anything
 * carrying more than the agreement itself is left to the assistant, so the
 * cost of being wrong here is always a turn the reader can repeat, never a
 * run they did not ask for.
 *
 * The word stays English in every language because the copy asks for it in
 * English in every language: the product must accept exactly the token it
 * printed.
 */
export function isPlainGo(text: string): boolean {
  return /^(go|go ahead)[.!]*$/i.test(text.trim())
}

/**
 * Whether this message is the reader approving a parked workflow gate.
 *
 * Same narrow shape as `isPlainGo`. "continue" / "go" / "yes" / "approve" on
 * their own are the agreement; anything with more words is a question for the
 * assistant (or a gate rewrite).
 */
export function isPlainGateContinue(text: string): boolean {
  return /^(continue|go|go ahead|yes|approve)[.!]*$/i.test(text.trim())
}

/**
 * Whether this message is the reader confirming a draft set, and nothing else.
 *
 * Same narrow shape as `isPlainGo`. "yes" / "confirm" / "save" on their own
 * are the agreement; "save and start" is a question for the assistant.
 */
export function isPlainCreativeConfirm(text: string): boolean {
  return /^(yes|confirm|save)[.!]*$/i.test(text.trim())
}

/** What a bare "go" does to one plan, or nothing when it is not waiting. */
function targetForPlan(
  messageId: string,
  plan: GoIntentPlan,
): ThreadGoTarget | null {
  if (plan.lifecycle === 'proposed') {
    return { messageId, mode: 'confirm' }
  }
  if (
    plan.lifecycle === 'active' &&
    plan.currentIndex !== null &&
    plan.steps[plan.currentIndex]?.status === 'current'
  ) {
    return { messageId, mode: 'start' }
  }
  return null
}

/**
 * The plan a bare "go" applies to, and what it should do to it.
 *
 * "go" answers whatever is still on screen as the last thing the assistant
 * said, and only that. Two turns invite it: the plan card itself, and the
 * card's run-completion announcement, which is the turn that literally asks
 * for a "go" and so is the common case, not an edge one. Anything else newer
 * than the card means the conversation moved on: a plan proposed at turn 5
 * and left unanswered while the thread ran on for another six exchanges is no
 * longer what "go ahead" is answering, and activating it would start a step
 * (an LLM turn, for a skill) the reader never agreed to.
 *
 * Between the card and its announcement sits the run and the step's own
 * prose, so the walk back from an announcement skips assistant turns that
 * carry no plan. `proposed` needs the approval Sounds good gives; `active`
 * with a step waiting starts that step. A plan that is running, done,
 * dismissed or superseded is not waiting for an answer, so "go" is an
 * ordinary question again.
 */
export function resolveGoTarget(
  turns: Array<GoIntentTurn>,
  plansByMessageId: Record<string, GoIntentPlan | undefined>,
): ThreadGoTarget | null {
  let newestIndex = -1
  for (let index = turns.length - 1; index >= 0; index -= 1) {
    if (turns[index].role === 'assistant') {
      newestIndex = index
      break
    }
  }
  if (newestIndex < 0) {
    return null
  }

  const newest = turns[newestIndex]
  const ownPlan = plansByMessageId[newest.id]
  if (ownPlan) {
    return targetForPlan(newest.id, ownPlan)
  }
  /* A step's own answer counts too. An approved plan runs its skill steps in
     the thread and stops at the first workflow step, so the newest turn is
     that skill's prose while a workflow step sits waiting: without this the
     reader's only way on is the card, which by then may be far up the
     column. */
  if (newest.planMessageId) {
    const stepPlan = plansByMessageId[newest.planMessageId]
    return stepPlan ? targetForPlan(newest.planMessageId, stepPlan) : null
  }
  if (!isWorkflowRunCompletionAskId(newest.askId)) {
    return null
  }

  for (let index = newestIndex - 1; index >= 0; index -= 1) {
    const turn = turns[index]
    if (turn.role !== 'assistant') {
      continue
    }
    const plan = plansByMessageId[turn.id]
    if (!plan) {
      continue
    }
    return targetForPlan(turn.id, plan)
  }
  return null
}

interface CreativeDraftIntentEntry {
  confirmedAt?: string | null
  publicId?: string | null
}

interface CreativeDraftIntentTurn {
  id: string
  role: string
  creativeDrafts?: Array<CreativeDraftIntentEntry>
}

export interface CreativeDraftConfirmTarget {
  messageId: string
}

function hasUnconfirmedCreativeDraft(
  drafts: Array<CreativeDraftIntentEntry> | undefined,
): boolean {
  if (!drafts || drafts.length === 0) {
    return false
  }
  return drafts.some((draft) => !draft.confirmedAt && !draft.publicId)
}

/**
 * The draft set a bare "yes" / "confirm" / "save" applies to.
 *
 * Only the newest assistant turn. An older unconfirmed set is not what that
 * word answers, even if it is still on screen.
 */
export function resolveCreativeDraftConfirmTarget(
  turns: Array<CreativeDraftIntentTurn>,
): CreativeDraftConfirmTarget | null {
  for (let index = turns.length - 1; index >= 0; index -= 1) {
    const turn = turns[index]
    if (turn.role !== 'assistant') {
      continue
    }
    if (hasUnconfirmedCreativeDraft(turn.creativeDrafts)) {
      return { messageId: turn.id }
    }
    return null
  }
  return null
}
